import { Switch, Route, Redirect } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ThemeProvider } from "@/components/theme-provider";
import { LanguageProvider } from "@/components/language-provider";
import { SettingsProvider } from "@/components/settings-provider";
import { NotificationPopup } from "@/components/notification-popup";
import { ErrorBoundary } from "@/components/error-boundary";
import NotFound from "@/pages/not-found";
import Login from "@/pages/login";
import ForgotPassword from "@/pages/forgot-password";
import ResetPassword from "@/pages/reset-password";
import Register from "@/pages/register";
import CompleteRegistration from "@/pages/complete-registration";
import Dashboard from "@/pages/dashboard";
import Documents from "@/pages/documents";
import DocumentDetail from "@/pages/document-detail";
import DocumentImport from "@/pages/document-import";
import Signatures from "@/pages/signatures";
import Settings from "@/pages/settings";
import VerifyPayment from "@/pages/verify-payment";
import Contacts from "@/pages/contacts";
import Leads from "@/pages/leads";
import { CrmRecordPage } from "@/components/crm/crm-record";
import Profile from "@/pages/profile";
import SystemSettings from "@/pages/admin";
import DocumentWizard from "@/pages/document-wizard";
import Sign from "@/pages/sign";
import AuthCallback from "@/pages/auth-callback";
import Dynamics365 from "@/pages/dynamics365";
import ApiWebhooks from "@/pages/api-webhooks";
import UploadGovBr from "@/pages/upload-govbr";
import Help from "@/pages/help";
import { SupportTicketPage, PublicSupportTicketPage } from "@/pages/support-ticket";
import { AccountDeleted, AccountRestore } from "@/pages/account-status";
import Verify from "@/pages/verify";
import PublicVerify from "@/pages/public-verify";
import VerifyPhone from "@/pages/verify-phone";
import AuditLogs from "@/pages/audit-logs";
import Notifications from "@/pages/notifications";
import { EnvironmentProvider } from './contexts/EnvironmentProvider';
import { EnvironmentBanner } from './components/EnvironmentBanner';
import { EnvironmentFavicon } from './components/EnvironmentFavicon';
import './styles/environment.css';
import DocumentTemplates from "./pages/document-templates";
import Library from "@/pages/library";
import LibraryModel from "@/pages/library-model";
import { AssistantHost } from "@/components/assistant/assistant-widget";

function Router() {
  return (
    <Switch>
      {/* Redirect root to login */}
      <Route path="/">
        <Redirect to="/login" />
      </Route>
      <Route path="/login" component={Login} />
      <Route path="/forgot-password" component={ForgotPassword} />
      <Route path="/reset-password" component={ResetPassword} />
      <Route path="/register" component={Register} />
      <Route path="/complete-registration" component={CompleteRegistration} />
      <Route path="/auth/google/callback">{() => <AuthCallback provider="google" />}</Route>
      <Route path="/auth/microsoft/callback">{() => <AuthCallback provider="microsoft" />}</Route>
      <Route path="/auth/callback">{() => <AuthCallback />}</Route>
      <Route path="/dashboard" component={Dashboard} />
      <Route path="/documents" component={Documents} />
      <Route path="/documents/new" component={DocumentWizard} />
      <Route path="/documents/import" component={DocumentImport} />
      <Route path="/documents/:id" component={DocumentDetail} />
      <Route path="/modelos" component={Library} />
      <Route path="/modelos/meus/:id" component={LibraryModel} />
      <Route path="/modelos/:slug" component={LibraryModel} />
      <Route path="/signatures" component={Signatures} />
      <Route path="/notifications" component={Notifications} />
      <Route path="/settings" component={Settings} />
      <Route path="/verify-payment" component={VerifyPayment} />
      <Route path="/contacts" component={Contacts} />
      <Route path="/contacts/:id">{(p) => <CrmRecordPage entity="contact" id={p.id} />}</Route>
      <Route path="/leads" component={Leads} />
      <Route path="/leads/:id">{(p) => <CrmRecordPage entity="lead" id={p.id} />}</Route>
      <Route path="/profile" component={Profile} />
      <Route path="/admin" component={SystemSettings} />
      <Route path="/sign/:token" component={Sign} />
      <Route path="/upload-signed/:token" component={UploadGovBr} />
      <Route path="/dynamics365" component={Dynamics365} />
      <Route path="/api-webhooks" component={ApiWebhooks} />
      <Route path="/help" component={Help} />
      <Route path="/ajuda/chamados" component={Help} />
      <Route path="/ajuda/chamados/:number" component={SupportTicketPage} />
      {/* Ticket link of the e-mail or WhatsApp (?t=token), without login */}
      <Route path="/chamado/:number" component={PublicSupportTicketPage} />
      {/* Excluir minha conta: right after it, and the undo link of the e-mail */}
      <Route path="/conta/excluida" component={AccountDeleted} />
      <Route path="/conta/restaurar" component={AccountRestore} />
      {/* Public document verification - accessible without login */}
      <Route path="/verificar" component={PublicVerify} />
      <Route path="/verificar/:code" component={PublicVerify} />
      <Route path="/verify" component={PublicVerify} />
      <Route path="/verify/:code" component={PublicVerify} />
      {/* Legacy verify route */}
      <Route path="/verify-document/:token" component={Verify} />
      <Route path="/verify-phone" component={VerifyPhone} />
      <Route path="/audit-logs" component={AuditLogs} />
      <Route path="/document-templates" component={DocumentTemplates} />
      <Route component={NotFound} />
    </Switch>
  );
}

function App() {
  return (
    <EnvironmentProvider>
      <EnvironmentFavicon />
      <EnvironmentBanner />
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <SettingsProvider>
          <LanguageProvider>
            <TooltipProvider>
              <Toaster />
              <NotificationPopup />
              <ErrorBoundary>
                <Router />
                <AssistantHost />
              </ErrorBoundary>
            </TooltipProvider>
          </LanguageProvider>
        </SettingsProvider>
      </ThemeProvider>
    </QueryClientProvider>
    </EnvironmentProvider>
  );
}

export default App;
